import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck, LogOut, Loader2, Trash2, Phone, Mail, MapPin, Calendar,
  Clock, RefreshCw, Lock, Settings as SettingsIcon, Bell, Send, ArrowLeft,
  StickyNote, CalendarDays, ChevronLeft, ChevronRight, List,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mhjg_admin_token";

const field =
  "h-12 rounded-none bg-black border border-[#222] text-white placeholder:text-[#666] focus-visible:ring-1 focus-visible:ring-[#FF4D00] focus-visible:ring-offset-0 focus-visible:border-[#FF4D00]";

const STATUSES = ["new", "contacted", "scheduled", "done"];
const STATUS_COLOR = {
  new: "text-[#FF4D00] border-[#FF4D00]",
  contacted: "text-yellow-400 border-yellow-500/50",
  scheduled: "text-blue-400 border-blue-500/50",
  done: "text-green-400 border-green-500/50",
};

const authAxios = (token) =>
  axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back");
      onLogin(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm border border-[#222] bg-[#0d0d0d] p-8">
        <div className="flex items-center gap-2 mb-8">
          <span className="flex h-9 w-9 items-center justify-center bg-[#FF4D00]"><Truck className="h-5 w-5 text-black" /></span>
          <span className="font-display text-lg uppercase tracking-tight leading-none">Mile High<br />Junk Guys</span>
        </div>
        <p className="font-mono-a text-[11px] tracking-[0.2em] uppercase text-[#FF4D00] flex items-center gap-2"><Lock className="h-3.5 w-3.5" /> Admin Access</p>
        <h1 className="font-display text-3xl uppercase tracking-tight mt-2 mb-6">Dashboard Login</h1>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Email</Label>
            <Input data-testid="admin-email" type="email" className={field} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Password</Label>
            <Input data-testid="admin-password" type="password" className={field} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full h-12 bg-[#FF4D00] hover:bg-[#FF6A00] text-black font-mono-a uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </button>
        </div>
        <a href="/" className="block mt-6 font-mono-a text-[11px] tracking-widest uppercase text-[#666] hover:text-[#FF4D00] transition-colors">← Back to site</a>
      </form>
    </div>
  );
}

function Row({ kind, item, onStatus, onDelete, onNote }) {
  const isBooking = kind === "bookings";
  const [note, setNote] = useState(item.admin_note || "");
  const [savingNote, setSavingNote] = useState(false);
  const dirty = note !== (item.admin_note || "");

  const saveNote = async () => {
    setSavingNote(true);
    await onNote(kind, item.id, note);
    setSavingNote(false);
  };

  return (
    <div data-testid={`admin-row-${item.id}`} className="border border-[#222] bg-[#0d0d0d] p-5 flex flex-col md:flex-row md:items-start gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-display text-xl uppercase tracking-tight">{item.name}</h3>
          <span className={`font-mono-a text-[10px] tracking-widest uppercase border px-2 py-0.5 ${STATUS_COLOR[item.status] || STATUS_COLOR.new}`}>{item.status}</span>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#aaa]">
          <a href={`tel:${item.phone}`} className="flex items-center gap-2 hover:text-[#FF4D00]"><Phone className="h-3.5 w-3.5 text-[#FF4D00]" /> {item.phone}</a>
          {item.email && <a href={`mailto:${item.email}`} className="flex items-center gap-2 hover:text-[#FF4D00] truncate"><Mail className="h-3.5 w-3.5 text-[#FF4D00]" /> {item.email}</a>}
          {item.service_type && <span className="flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-[#FF4D00]" /> {item.service_type}</span>}
          {isBooking && <span className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-[#FF4D00]" /> {item.preferred_date}</span>}
          {isBooking && <span className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-[#FF4D00]" /> {item.preferred_time}</span>}
          {isBooking && item.address && <span className="flex items-center gap-2 sm:col-span-2"><MapPin className="h-3.5 w-3.5 text-[#FF4D00]" /> {item.address}</span>}
        </div>
        {(item.message || item.notes) && (
          <p className="mt-3 text-sm text-[#888] border-l-2 border-[#FF4D00]/40 pl-3">{item.message || item.notes}</p>
        )}
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-1.5">
            <StickyNote className="h-3.5 w-3.5 text-[#FF4D00]" />
            <span className="font-mono-a text-[10px] tracking-widest uppercase text-[#888]">Crew Note (private)</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <textarea
              data-testid={`note-input-${item.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. gate code 1234, heavy safe in basement, park in alley..."
              className="flex-1 rounded-none bg-black border border-[#222] text-white text-sm p-3 placeholder:text-[#555] focus:outline-none focus:border-[#FF4D00] resize-y"
            />
            <button
              data-testid={`note-save-${item.id}`}
              onClick={saveNote}
              disabled={!dirty || savingNote}
              className="h-10 sm:h-auto px-4 border border-[#FF4D00] text-[#FF4D00] hover:bg-[#FF4D00] hover:text-black font-mono-a text-xs uppercase tracking-widest transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#FF4D00] flex items-center justify-center gap-2 shrink-0"
            >
              {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Note"}
            </button>
          </div>
        </div>
        <p className="mt-3 font-mono-a text-[10px] tracking-widest uppercase text-[#555]">{new Date(item.created_at).toLocaleString()}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Select value={item.status} onValueChange={(v) => onStatus(kind, item.id, v)}>
          <SelectTrigger data-testid={`status-${item.id}`} className="h-10 w-36 rounded-none bg-black border-[#222] text-white text-xs focus:ring-1 focus:ring-[#FF4D00]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0a] border-[#222] text-white rounded-none">
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-[#FF4D00] focus:text-black rounded-none capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <button data-testid={`delete-${item.id}`} onClick={() => onDelete(kind, item.id)} className="h-10 w-10 flex items-center justify-center border border-[#222] text-[#888] hover:text-white hover:bg-red-600/80 hover:border-red-600 transition-colors">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function CalendarView({ bookings, onStatus, onDelete, onNote }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState(null);

  const byDate = {};
  for (const b of bookings) {
    if (!b.preferred_date) continue;
    (byDate[b.preferred_date] = byDate[b.preferred_date] || []).push(b);
  }

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const fmt = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  const selectedList = selected ? byDate[selected] || [] : [];

  const shift = (n) => { setCursor(new Date(year, month + n, 1)); setSelected(null); };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl md:text-3xl uppercase tracking-tight">{MONTHS[month]} {year}</h2>
        <div className="flex gap-2">
          <button data-testid="cal-prev" onClick={() => shift(-1)} className="h-10 w-10 flex items-center justify-center border border-[#222] text-[#aaa] hover:text-[#FF4D00] hover:border-[#FF4D00] transition-colors"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="cal-today" onClick={() => { setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); setSelected(null); }} className="h-10 px-4 border border-[#222] text-[#aaa] hover:text-[#FF4D00] hover:border-[#FF4D00] font-mono-a text-xs uppercase tracking-widest transition-colors">Today</button>
          <button data-testid="cal-next" onClick={() => shift(1)} className="h-10 w-10 flex items-center justify-center border border-[#222] text-[#aaa] hover:text-[#FF4D00] hover:border-[#FF4D00] transition-colors"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-l border-t border-[#222]">
        {DOW.map((d) => (
          <div key={d} className="border-r border-b border-[#222] bg-[#0d0d0d] py-2 text-center font-mono-a text-[10px] tracking-widest uppercase text-[#888]">{d}</div>
        ))}
        {cells.map((d, i) => {
          const key = d ? fmt(d) : `e${i}`;
          const count = d ? (byDate[fmt(d)]?.length || 0) : 0;
          const isSel = d && selected === fmt(d);
          return (
            <button
              key={key}
              data-testid={d ? `cal-day-${fmt(d)}` : undefined}
              onClick={() => d && count && setSelected(fmt(d))}
              disabled={!d || !count}
              className={`min-h-[74px] md:min-h-[92px] border-r border-b border-[#222] p-2 text-left align-top transition-colors ${!d ? "bg-[#080808]" : count ? "bg-[#0d0d0d] hover:bg-[#161616] cursor-pointer" : "bg-[#0a0a0a] cursor-default"} ${isSel ? "ring-1 ring-inset ring-[#FF4D00]" : ""}`}
            >
              {d && (
                <>
                  <span className={`font-mono-a text-xs ${isToday(d) ? "text-black bg-[#FF4D00] px-1.5 py-0.5" : "text-[#ccc]"}`}>{d}</span>
                  {count > 0 && (
                    <div className="mt-2 flex items-center gap-1">
                      <span className="h-1.5 w-1.5 bg-[#FF4D00]" />
                      <span className="font-mono-a text-[10px] tracking-widest uppercase text-[#FF4D00]">{count} pickup{count > 1 ? "s" : ""}</span>
                    </div>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>

      {selected && (
        <div className="mt-6">
          <h3 className="font-display text-xl uppercase tracking-tight mb-3">Pickups on {selected}</h3>
          <div className="space-y-3">
            {selectedList.map((item) => (
              <Row key={item.id} kind="bookings" item={item} onStatus={onStatus} onDelete={onDelete} onNote={onNote} />
            ))}
          </div>
        </div>
      )}
      {!selected && (
        <p className="mt-6 font-mono-a text-xs tracking-widest uppercase text-[#666]">Click a highlighted day to see its pickups</p>
      )}
    </div>
  );
}

function SettingsPanel({ token, onTokenChange }) {
  const api = authAxios(token);
  const [s, setS] = useState(null);
  const [savingSms, setSavingSms] = useState(false);
  const [testing, setTesting] = useState(false);
  const [cred, setCred] = useState({ current_password: "", new_email: "", new_password: "" });
  const [savingCred, setSavingCred] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => toast.error("Failed to load settings"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSms = async () => {
    setSavingSms(true);
    try {
      await api.put("/admin/settings", { sms_enabled: s.sms_enabled, sms_phone: s.sms_phone, sms_carrier: s.sms_carrier });
      toast.success("Alert settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingSms(false); }
  };

  const testSms = async () => {
    if (!s.sms_phone || !s.sms_carrier) {
      toast.error("Enter your cell number and pick your carrier first");
      return;
    }
    setTesting(true);
    try {
      // save current selection first so the test uses exactly what's on screen
      await api.put("/admin/settings", { sms_enabled: s.sms_enabled, sms_phone: s.sms_phone, sms_carrier: s.sms_carrier });
      await api.post("/admin/test-sms");
      toast.success("Test text sent! Check your phone.");
    }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Test failed"); }
    finally { setTesting(false); }
  };

  const saveCred = async (e) => {
    e.preventDefault();
    if (!cred.current_password) { toast.error("Enter your current password"); return; }
    if (!cred.new_email && !cred.new_password) { toast.error("Enter a new email or password"); return; }
    setSavingCred(true);
    try {
      const payload = { current_password: cred.current_password };
      if (cred.new_email) payload.new_email = cred.new_email;
      if (cred.new_password) payload.new_password = cred.new_password;
      const { data } = await api.post("/auth/change-credentials", payload);
      localStorage.setItem(TOKEN_KEY, data.token);
      onTokenChange(data.token);
      toast.success("Login updated");
      setCred({ current_password: "", new_email: "", new_password: "" });
      setS((p) => ({ ...p, admin_email: data.user.email }));
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Update failed");
    } finally { setSavingCred(false); }
  };

  if (!s) return <div className="flex items-center justify-center py-24 text-[#888]"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
      {/* Text alerts */}
      <div className="border border-[#222] bg-[#0d0d0d] p-6">
        <div className="flex items-center gap-2 mb-1"><Bell className="h-4 w-4 text-[#FF4D00]" /><h2 className="font-display text-2xl uppercase tracking-tight">Text Alerts</h2></div>
        <p className="text-[#888] text-sm mb-6">Get a free text the moment a lead comes in — sent through your phone carrier's email-to-text gateway. No Twilio, no cost.</p>
        <div className="flex items-center justify-between border border-[#222] px-4 h-14 mb-4">
          <span className="font-mono-a text-xs uppercase tracking-widest text-[#ccc]">Enable text alerts</span>
          <Switch data-testid="sms-toggle" checked={s.sms_enabled} onCheckedChange={(v) => setS((p) => ({ ...p, sms_enabled: v }))} />
        </div>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Cell number for alerts</Label>
            <Input data-testid="sms-phone" className={field} value={s.sms_phone} onChange={(e) => setS((p) => ({ ...p, sms_phone: e.target.value }))} placeholder="7206652341" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Phone carrier</Label>
            <Select value={s.sms_carrier} onValueChange={(v) => setS((p) => ({ ...p, sms_carrier: v }))}>
              <SelectTrigger data-testid="sms-carrier" className={field}><SelectValue placeholder="Select carrier" /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#222] text-white rounded-none max-h-64">
                {s.carriers.map((c) => <SelectItem key={c} value={c} className="focus:bg-[#FF4D00] focus:text-black rounded-none">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <button data-testid="sms-save" onClick={saveSms} disabled={savingSms} className="flex-1 h-12 bg-[#FF4D00] hover:bg-[#FF6A00] text-black font-mono-a uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {savingSms ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </button>
            <button data-testid="sms-test" onClick={testSms} disabled={testing} className="h-12 px-5 border border-[#FF4D00] text-[#FF4D00] hover:bg-[#FF4D00] hover:text-black font-mono-a uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Test
            </button>
          </div>
        </div>
      </div>

      {/* Credentials */}
      <div className="border border-[#222] bg-[#0d0d0d] p-6">
        <div className="flex items-center gap-2 mb-1"><Lock className="h-4 w-4 text-[#FF4D00]" /><h2 className="font-display text-2xl uppercase tracking-tight">Login & Password</h2></div>
        <p className="text-[#888] text-sm mb-2">Signed in as <span className="text-white">{s.admin_email}</span></p>
        <p className="text-[#666] text-xs mb-6">Enter your current password to change your email and/or password.</p>
        <form onSubmit={saveCred} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Current password *</Label>
            <Input data-testid="cred-current" type="password" className={field} value={cred.current_password} onChange={(e) => setCred((c) => ({ ...c, current_password: e.target.value }))} placeholder="••••••••" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">New email</Label>
            <Input data-testid="cred-email" type="email" className={field} value={cred.new_email} onChange={(e) => setCred((c) => ({ ...c, new_email: e.target.value }))} placeholder="leave blank to keep" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">New password</Label>
            <Input data-testid="cred-password" type="password" className={field} value={cred.new_password} onChange={(e) => setCred((c) => ({ ...c, new_password: e.target.value }))} placeholder="leave blank to keep" />
          </div>
          <button data-testid="cred-save" type="submit" disabled={savingCred} className="w-full h-12 bg-[#FF4D00] hover:bg-[#FF6A00] text-black font-mono-a uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {savingCred ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Login"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout, onTokenChange }) {
  const [view, setView] = useState("leads");
  const [leadView, setLeadView] = useState("list");
  const [tab, setTab] = useState("bookings");
  const [data, setData] = useState({ bookings: [], quotes: [], contacts: [] });
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const api = authAxios(token);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, q, c, s] = await Promise.all([
        api.get("/admin/bookings"), api.get("/admin/quotes"),
        api.get("/admin/contacts"), api.get("/admin/summary"),
      ]);
      setData({ bookings: b.data, quotes: q.data, contacts: c.data });
      setSummary(s.data);
    } catch (err) {
      if (err.response?.status === 401) { onLogout(); return; }
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (kind, id, status) => {
    setData((d) => ({ ...d, [kind]: d[kind].map((i) => (i.id === id ? { ...i, status } : i)) }));
    try { await api.patch(`/admin/${kind}/${id}`, { status }); toast.success("Status updated"); load(); }
    catch { toast.error("Update failed"); }
  };

  const onDelete = async (kind, id) => {
    try {
      await api.delete(`/admin/${kind}/${id}`);
      setData((d) => ({ ...d, [kind]: d[kind].filter((i) => i.id !== id) }));
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const onNote = async (kind, id, admin_note) => {
    setData((d) => ({ ...d, [kind]: d[kind].map((i) => (i.id === id ? { ...i, admin_note } : i)) }));
    try { await api.patch(`/admin/${kind}/${id}`, { admin_note }); toast.success("Note saved"); }
    catch { toast.error("Failed to save note"); }
  };

  const TABS = [
    { key: "bookings", label: "Bookings" },
    { key: "quotes", label: "Quotes" },
    { key: "contacts", label: "Messages" },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-[#222] px-4 md:px-10 h-16 flex items-center justify-between sticky top-0 bg-[#050505]/95 backdrop-blur z-40">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center bg-[#FF4D00]"><Truck className="h-4 w-4 text-black" /></span>
          <span className="font-display text-base uppercase tracking-tight">Junk Guys · Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="admin-view-toggle" onClick={() => setView((v) => (v === "leads" ? "settings" : "leads"))} className="h-10 px-4 border border-[#FF4D00] text-[#FF4D00] hover:bg-[#FF4D00] hover:text-black flex items-center gap-2 font-mono-a text-xs uppercase tracking-widest transition-colors">
            {view === "leads" ? <SettingsIcon className="h-4 w-4" /> : <ArrowLeft className="h-4 w-4" />}
            <span>{view === "leads" ? "Settings" : "Leads"}</span>
          </button>
          {view === "leads" && (
            <button data-testid="admin-refresh" onClick={load} className="h-10 px-3 border border-[#222] text-[#aaa] hover:text-[#FF4D00] flex items-center gap-2 font-mono-a text-xs uppercase tracking-widest transition-colors">
              <RefreshCw className="h-4 w-4" /> <span className="hidden sm:inline">Refresh</span>
            </button>
          )}
          <button data-testid="admin-logout" onClick={onLogout} className="h-10 px-3 border border-[#222] text-[#aaa] hover:text-white hover:bg-red-600/80 hover:border-red-600 flex items-center gap-2 font-mono-a text-xs uppercase tracking-widest transition-colors">
            <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <main className="px-4 md:px-10 py-8 max-w-6xl mx-auto">
        <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight mb-6">{view === "settings" ? "Settings" : "Leads Dashboard"}</h1>
        {view === "settings" ? (
          <SettingsPanel token={token} onTokenChange={onTokenChange} />
        ) : (
        <>
        <div className="grid grid-cols-3 gap-3 mb-8">
          {TABS.map((t) => (
            <div key={t.key} className="border border-[#222] bg-[#0d0d0d] p-4">
              <p className="font-mono-a text-[10px] tracking-widest uppercase text-[#888]">{t.label}</p>
              <p className="font-display text-3xl md:text-4xl mt-1">{summary[t.key]?.total ?? 0}</p>
              <p className="font-mono-a text-[10px] tracking-widest uppercase text-[#FF4D00] mt-1">{summary[t.key]?.new ?? 0} new</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6">
          <button data-testid="leadview-list" onClick={() => setLeadView("list")} className={`h-10 px-4 border flex items-center gap-2 font-mono-a text-xs uppercase tracking-widest transition-colors ${leadView === "list" ? "border-[#FF4D00] text-[#FF4D00]" : "border-[#222] text-[#777] hover:text-white"}`}>
            <List className="h-4 w-4" /> List
          </button>
          <button data-testid="leadview-calendar" onClick={() => setLeadView("calendar")} className={`h-10 px-4 border flex items-center gap-2 font-mono-a text-xs uppercase tracking-widest transition-colors ${leadView === "calendar" ? "border-[#FF4D00] text-[#FF4D00]" : "border-[#222] text-[#777] hover:text-white"}`}>
            <CalendarDays className="h-4 w-4" /> Calendar
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#888]"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : leadView === "calendar" ? (
          <CalendarView bookings={data.bookings} onStatus={onStatus} onDelete={onDelete} onNote={onNote} />
        ) : (
        <>
        <div className="flex gap-2 mb-6 border-b border-[#222]">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`font-mono-a text-xs uppercase tracking-widest px-4 py-3 border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#FF4D00] text-white" : "border-transparent text-[#777] hover:text-white"}`}>
              {t.label} ({data[t.key].length})
            </button>
          ))}
        </div>

        {data[tab].length === 0 ? (
          <div className="border border-dashed border-[#222] py-24 text-center text-[#666] font-mono-a text-sm uppercase tracking-widest">No {tab} yet</div>
        ) : (
          <div className="space-y-3">
            {data[tab].map((item) => (
              <Row key={item.id} kind={tab} item={item} onStatus={onStatus} onDelete={onDelete} onNote={onNote} />
            ))}
          </div>
        )}
        </>
        )}
        </>
        )}
      </main>
    </div>
  );
}

export default function AdminDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast.info("Logged out");
  };

  if (!token) return <LoginScreen onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} onTokenChange={setToken} />;
}
